import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ChevronUp, ChevronDown, CornerDownRight } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Reusable modal for editing pantry / shopping category-chip rearrangement.
 * Supports reorder (up/down) and assigning a category as a sub-group of another.
 *
 * Props:
 *   open, onOpenChange
 *   boatId               – active boat / household id
 *   categories           – string[] of currently-present category keys
 *   labels               – { [key]: displayLabel }
 *   initialOrder         – string[] current saved order (used as starting state)
 *   initialSubgroups     – { [child]: parent } current saved mapping
 *   onSaved              – callback after successful save (e.g. reload boat data)
 */
export default function EditCategoryGroupsDialog({
  open,
  onOpenChange,
  boatId,
  categories,
  labels = {},
  initialOrder = [],
  initialSubgroups = {},
  onSaved,
}) {
  const [order, setOrder] = useState([]);
  const [subgroups, setSubgroups] = useState({});
  const [saving, setSaving] = useState(false);

  // Reset state every time dialog opens so the user sees the latest values
  useEffect(() => {
    if (!open) return;
    // Start with saved order, then append any new categories not yet ordered (alphabetical)
    const set = new Set(initialOrder);
    const extras = categories.filter((c) => !set.has(c)).sort((a, b) => a.localeCompare(b, "nb"));
    setOrder([...initialOrder.filter((c) => categories.includes(c)), ...extras]);
    setSubgroups({ ...initialSubgroups });
  }, [open, initialOrder, initialSubgroups, categories]);

  // Categories that other rows have already designated as a child – these
  // cannot themselves act as parents (one-level nesting only).
  const childKeys = useMemo(() => new Set(Object.keys(subgroups)), [subgroups]);

  const move = (idx, dir) => {
    const next = [...order];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setOrder(next);
  };

  const setParent = (child, parent) => {
    setSubgroups((prev) => {
      const next = { ...prev };
      if (!parent || parent === child) delete next[child];
      else next[child] = parent;
      return next;
    });
  };

  const save = async () => {
    if (!boatId) return;
    setSaving(true);
    try {
      await Promise.all([
        api.updateBoatCategoryOrder(boatId, order),
        api.updateBoatCategorySubgroups(boatId, subgroups),
      ]);
      toast.success("Fane-oppsett lagret");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikke lagre oppsett");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="edit-category-groups-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }}>Rediger faner</DialogTitle>
          <DialogDescription>
            Endre rekkefølge med pilene, og velg om en kategori skal være under-gruppe av en annen (f.eks. Grillmat under Kjøtt).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1 mt-2">
          {order.length === 0 && (
            <p className="text-sm text-stone-400 text-center py-6">Ingen kategorier å redigere ennå.</p>
          )}
          {order.map((cat, idx) => {
            const isChild = !!subgroups[cat];
            const parentOptions = order.filter((o) => o !== cat && !childKeys.has(o));
            return (
              <div
                key={cat}
                className={`flex items-center gap-2 py-2 px-2 rounded border ${
                  isChild ? "border-stone-100 bg-stone-50 ml-6" : "border-stone-100 bg-white"
                }`}
                data-testid={`edit-cat-row-${cat}`}
              >
                <div className="flex flex-col">
                  <button
                    type="button"
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0}
                    className="p-0.5 text-stone-500 hover:text-navy-800 disabled:opacity-30"
                    title="Flytt opp"
                    data-testid={`edit-cat-up-${cat}`}
                  >
                    <ChevronUp className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => move(idx, 1)}
                    disabled={idx === order.length - 1}
                    className="p-0.5 text-stone-500 hover:text-navy-800 disabled:opacity-30"
                    title="Flytt ned"
                    data-testid={`edit-cat-down-${cat}`}
                  >
                    <ChevronDown className="h-4 w-4" />
                  </button>
                </div>
                {isChild && <CornerDownRight className="h-3.5 w-3.5 text-stone-400" />}
                <span className="flex-1 text-sm font-medium text-stone-700">
                  {labels[cat] || cat}
                </span>
                <select
                  value={subgroups[cat] || ""}
                  onChange={(e) => setParent(cat, e.target.value)}
                  className="rounded-md border border-stone-200 bg-white px-2 py-1 text-xs text-stone-700 max-w-[160px] focus:outline-none focus:border-navy-800"
                  data-testid={`edit-cat-parent-${cat}`}
                >
                  <option value="">Egen fane</option>
                  {parentOptions.map((p) => (
                    <option key={p} value={p}>↳ Under {labels[p] || p}</option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="px-3 py-2 text-sm font-medium text-stone-600 hover:text-stone-800"
            data-testid="edit-cat-cancel"
          >
            Avbryt
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="rounded-md bg-navy-800 px-4 py-2 text-sm font-medium text-white hover:bg-navy-900 transition-colors disabled:opacity-60"
            data-testid="edit-cat-save"
          >
            {saving ? "Lagrer…" : "Lagre"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
